import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HONOR_CODE_LINES, HONOR_CODE_SUBLINE } from '../../../constants/honorCode';
import { useAuth } from '../../../contexts/AuthContext';
import {
	fetchChallengeInvite,
	respondToChallengeInvite
} from '../../../utils/challengeInviteApi';
import { inviteToChallengePreview } from '../../../utils/challengeInviteDisplay';
import ChallengeCard, { challengeCardBtn } from '../../ChallengeWire/ChallengeCard';
import { nestedPanelClass } from '../../ChallengeWire/challengeWireFormStyles';

export default function ChallengeInvitePage({
	action,
	getChallengeInviteEndpoint,
	acceptChallengeInviteEndpoint,
	declineChallengeInviteEndpoint
}) {
	const { token } = useParams();
	const navigate = useNavigate();
	const { authenticated, loading: authLoading } = useAuth();
	const [invite, setInvite] = useState(null);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [resultMessage, setResultMessage] = useState('');

	const loadInvite = useCallback(async () => {
		if (!token || !getChallengeInviteEndpoint) {
			setLoading(false);
			return;
		}

		setLoading(true);
		setError('');

		try {
			const data = await fetchChallengeInvite({
				token,
				endpoint: getChallengeInviteEndpoint
			});
			setInvite(data);
		} catch (err) {
			setInvite(null);
			setError(err.message);
		} finally {
			setLoading(false);
		}
	}, [getChallengeInviteEndpoint, token]);

	useEffect(() => {
		loadInvite();
	}, [loadInvite]);

	const submitResponse = useCallback(
		async (responseAction) => {
			const endpoint =
				responseAction === 'accept' ? acceptChallengeInviteEndpoint : declineChallengeInviteEndpoint;
			if (!token || !endpoint) return;

			setSubmitting(true);
			setError('');

			try {
				const data = await respondToChallengeInvite({
					token,
					endpoint,
					action: responseAction
				});

				if (responseAction === 'accept') {
					if (authenticated) {
						navigate('/challenge-wire', { replace: true });
					} else {
						navigate(
							`/signin?returnTo=${encodeURIComponent('/challenge-wire')}`,
							{ replace: true }
						);
					}
					return;
				}

				setResultMessage(data.message || 'Done.');
				await loadInvite();
			} catch (err) {
				setError(err.message);
			} finally {
				setSubmitting(false);
			}
		},
		[
			acceptChallengeInviteEndpoint,
			authenticated,
			declineChallengeInviteEndpoint,
			loadInvite,
			navigate,
			token
		]
	);

	useEffect(() => {
		if (authLoading || !invite?.pending || !action || submitting || resultMessage) return;
		if (action === 'accept' || action === 'decline') {
			submitResponse(action);
		}
	}, [action, authLoading, invite, resultMessage, submitResponse, submitting]);

	const challengePreview = useMemo(() => inviteToChallengePreview(invite), [invite]);
	const resolved = invite && !invite.pending;
	const showActions = invite?.pending && !action && !resultMessage;

	return (
		<div className="container p-4 pt-8 lg:py-16 md:px-20 max-w-2xl mx-auto">
			<h1 className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-dark-text">
				{action === 'accept' ? 'Accepting challenge…' : action === 'decline' ? 'Declining challenge…' : 'Challenge invite'}
			</h1>

			{loading && (
				<p className="mt-8 text-sm text-gray-500 dark:text-dark-text-secondary">Loading challenge…</p>
			)}

			{error && (
				<div className="mt-6 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card p-5 shadow-sm dark:shadow-black/20">
					<p className="text-sm text-red-600 dark:text-red-400" role="alert">
						{error}
					</p>
				</div>
			)}

			{resultMessage && (
				<div className="mt-6 rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card p-6 shadow-sm dark:shadow-black/20">
					<p className="text-base font-medium text-gray-900 dark:text-dark-text">{resultMessage}</p>
				</div>
			)}

			{challengePreview && !loading && (
				<div className="mt-6">
					<ChallengeCard challenge={challengePreview}>
						{resolved && (
							<p className="w-full basis-full text-sm font-medium text-gray-700 dark:text-dark-text-secondary">
								This invite is no longer pending (status: {invite.status.replace(/_/g, ' ')}).
							</p>
						)}

						{showActions && (
							<>
								<div className={`${nestedPanelClass} w-full basis-full`}>
									<p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-dark-text-secondary">
										{HONOR_CODE_SUBLINE}
									</p>
									{HONOR_CODE_LINES.map((line) => (
										<p key={line} className="text-sm italic text-gray-800 dark:text-dark-text">
											{line}
										</p>
									))}
									<p className="text-xs text-gray-500 dark:text-dark-text-secondary">
										Accepting means you agree to this honor code for this challenge.
									</p>
								</div>

								<button
									type="button"
									disabled={submitting || authLoading}
									onClick={() => submitResponse('accept')}
									className={challengeCardBtn.primary}
								>
									{submitting ? 'Saving…' : 'Accept'}
								</button>
								<button
									type="button"
									disabled={submitting || authLoading}
									onClick={() => submitResponse('decline')}
									className={challengeCardBtn.secondary}
								>
									Decline
								</button>
							</>
						)}

						{submitting && action && (
							<p className="w-full basis-full text-sm text-gray-500 dark:text-dark-text-secondary">
								Please wait…
							</p>
						)}
					</ChallengeCard>
				</div>
			)}
		</div>
	);
}
